import { assertPrecondition, mfsError } from '../../errors';
import {
  hasProperty,
  hasPropertyDefined,
  isNullOrWhitespace,
  isString,
  LABEL_ID_PROPERTY,
  LABEL_PROPERTY,
} from '../../utility';
import { AppDataOf, MfsItem, MfsItemId } from '../mfsTypes';
import { MfsValue } from '../mfsValue';

/** Represents the labelId value when it is explicitly set as undefined during update item. */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const RESET_LABEL_ID = '' as MfsItemId;

export type MfsLabelProperties = Pick<MfsItem, 'label' | 'labelId'>;

export type LabelTracker = {
  /** Claims the given label+labelId for the MFS item with itemId. */
  track(label: string, labelId: string, itemId: string): Promise<void>;

  /** Releases the given label+labelId. */
  untrack(label: string, labelId: string): Promise<void>;

  /** Gets the most recent itemId associated with the given label and labelId. */
  getItemId(label: string, labelId: string): string | undefined;

  /** Release all labels in the registry, remove all listeners. */
  cleanUp(): void;
};

/** Gets labelId from the patch, if any. If found, ensures it's valid. */
export const LabelTracker = {
  tryGetLabelIdFromPatch: <T extends MfsItem>(patch: Partial<AppDataOf<T>>): string | undefined => {
    if (!hasProperty(patch, LABEL_ID_PROPERTY)) {
      return undefined;
    }

    if (patch.labelId === undefined) {
      // indicate that the labelId is explicitly set as `undefined` in the patch (and thus will be released from tracking).
      return RESET_LABEL_ID;
    }

    // if the labelId is defined, it must be valid (non empty, non whitespace, non null).
    const labelId = patch.labelId;
    assertPrecondition(isString(labelId) && isValidLabelPropertyValue(labelId), mfsError.invalidLabelId);
    return labelId;
  },
  tryGetLabelId: (propertyName: string, value: MfsValue): string | undefined => {
    if (propertyName !== LABEL_ID_PROPERTY) {
      return undefined;
    }

    if (value === undefined) {
      // indicate that the labelId is explicitly set as `undefined` in the patch (and thus will be released from tracking).
      return RESET_LABEL_ID;
    }

    assertPrecondition(isString(value) && isValidLabelPropertyValue(value), mfsError.invalidLabelId);
    return value;
  },
  tryGetLabelAndId: (item: MfsItem): Required<MfsLabelProperties> | undefined => {
    if (hasPropertyDefined(item, LABEL_ID_PROPERTY) && hasProperty(item, LABEL_PROPERTY)) {
      assertPrecondition(isValidLabelPropertyValue(item.label), mfsError.invalidLabel);
      assertPrecondition(isValidLabelPropertyValue(item.labelId), mfsError.invalidLabelId);
      return { label: item.label, labelId: item.labelId };
    }
    return undefined;
  },
};

/** Checks if the given value is valid label or label id. */
export const isValidLabelPropertyValue = (value: string): boolean => !isNullOrWhitespace(value);
